import { ApiError } from '@/teoCore/errors/ApiError';
import { FlatStore } from '@/teoCore/state/FlatState';
import { TeoCore } from '@/teoCore/TeoCore';

import type { TeoText } from '../TeoText';
import {
  areAllPromptOptionsSelected,
  extractSelectedOptionsFromPromptTemplate,
} from '../TeoText.helpers';
import { GeneratorHistoryEntry } from './GeneratorHistoryEntry';
import type { GeneratorStoredState } from './GeneratorStore.type';

export class GeneratorStore extends FlatStore<GeneratorStore>() {
  history: GeneratorHistoryEntry[];
  selectedHistory: number;
  loading: boolean;

  #text: TeoText;

  constructor(text: TeoText, initialState: Partial<GeneratorStoredState> = {}) {
    super();
    this.#text = text;
    this.history =
      initialState.history?.length ?
        initialState?.history?.map((h) => new GeneratorHistoryEntry(h))
      : [new GeneratorHistoryEntry()];
    this.selectedHistory = initialState?.selectedHistory ?? 0;
    this.loading = false;
    this.addEventListener('*', () => this.#text.save());
    this.addEventListener('selectedHistory', () => {
      if (this.currentHistory?.textData) {
        this.#text.updateTextData(this.currentHistory.textData);
      }
    });
    text.store.addEventListener('currentTemplateId', (newTemplateId) => {
      if (
        this.currentHistory.locked &&
        this.currentHistory.textData?.storeState?.currentTemplateId !== newTemplateId
      ) {
        this.createNewCopy({ textData: this.#text.getTextData() });
      }
    });
  }

  get historyPageInfo() {
    return {
      total: this.history.length,
      current: this.selectedHistory,
    };
  }

  get currentHistory() {
    const currentHistory = this.history[this.selectedHistory];
    if (!currentHistory) {
      throw new Error('Error getting current history');
    }
    return currentHistory;
  }

  selectHistory(selectedHistory: number | GeneratorHistoryEntry) {
    if (typeof selectedHistory === 'number') {
      if (selectedHistory === this.selectedHistory) {
        return;
      }
      if (selectedHistory < 0 || selectedHistory >= this.history.length) {
        this.setStateValue('selectedHistory', 0);
      } else {
        this.setStateValue('selectedHistory', selectedHistory);
      }
    } else {
      this.selectHistory(this.history.indexOf(selectedHistory));
    }
  }

  async generate() {
    const textData = this.#text.getBaseTextData();
    const original = this.currentHistory;
    // make sure current textdata is set on the eventually "old" entry.
    original.textData = textData;
    // if it is already locked, create a new copy
    const entry =
      original.locked ?
        this.createNewCopy({
          generatorError: null,
        })
      : original;

    try {
      if (entry.locked) {
        throw new Error('Assert: currentHistory may not be locked for text generation.');
      }
      // call generator api
      const templateId = textData.storeState?.currentTemplateId;
      const selectedOptions = textData.storeState?.selectedPromptOptions;
      if (!templateId) {
        throw new Error('Konfiguration ungültig – Keine ID.');
      }
      const template = TeoCore.current.dynamicConfig.getTemplateById(templateId);
      if (!template) {
        throw new Error('Assert: template is required for text generation');
      }
      if (!selectedOptions || !areAllPromptOptionsSelected(template, selectedOptions)) {
        throw new Error('Konfiguration ungültig – Optionen nicht vollständig.');
      }
      this.setStateValue('loading', true);
      const result = await TeoCore.current.api.getAiTextGeneration({
        evaluation_profile_id: TeoCore.current.staticConfig.evaluationProfile,
        template_id: templateId,
        text_content_info: entry.instructions,
        selected_options: extractSelectedOptionsFromPromptTemplate(template, selectedOptions),
      });
      if (ApiError.matches(result)) {
        throw result;
      }
      const newText = result.text ?? '';
      this.#text.isEditable = true;
      this.#text.updateText(newText);
      this.#text.isEditable = false;
      this.#text.events.triggerEvent('textChanged', newText);
      this.#text.analysisConfiguration = result.analysisConfig;
      this.#text.updateResult(result.analysisResult, result.text);
      entry.textData = this.#text.getTextData();
      this.#text.events.triggerEvent('textChanged', newText);
    } catch (error) {
      entry.generatorError = {
        message: error instanceof Error ? error.message : 'Unbekannter Fehler',
      };
    } finally {
      this.setStateValue('loading', false);
    }

    entry.locked = !entry.generatorError;
    this.setStateValue('history', this.history, true);
    return entry;
  }

  toJSON(): GeneratorStoredState {
    return {
      history: this.history, // .map((h) => h.clone()),
      selectedHistory: this.selectedHistory,
    };
  }

  updateInstructions(instructions: string) {
    if (this.currentHistory.locked) {
      this.createNewCopy({ instructions });
    } else {
      this.currentHistory.instructions = instructions;
      this.setStateValue('history', this.history, true);
    }
  }

  createNewCopy(changes?: Partial<GeneratorHistoryEntry>) {
    const original = this.currentHistory;
    const newHistory = original.clone();
    newHistory.locked = changes?.locked ?? false;
    newHistory.generatorError = changes?.generatorError ?? null;
    if (changes?.instructions) {
      newHistory.instructions = changes.instructions;
    }
    if (changes?.textData) {
      newHistory.textData = changes.textData;
    }
    const currentHistories = this.history;
    this.setStateValue('history', [...currentHistories, newHistory]);
    this.setStateValue('selectedHistory', currentHistories.length);
    return newHistory;
  }
}
